/** Types + helpers for Execution_RFO_Dashboard.json (Revenue Analytical Dashboard) */

export type StringNumberMap = Record<string, number>;

/** Keys are omitted when optional columns were not mapped / analytics not produced for that run. */
export type RevenueDashboardBlock = Partial<{
  "MoM Trend": StringNumberMap;
  "Customer Wise Analysis": StringNumberMap;
  "Incoterm Wise Analysis": StringNumberMap;
  "Export/Domestic Analysis": StringNumberMap;
  "Product Type Analysis": StringNumberMap;
  "Product Rate Analysis (MoM)": Record<string, StringNumberMap>;
  "MoM Quantity vs Value (Total)": Record<string, { billing_quantity: number; invoice_amount: number }>;
  "MoM Quantity vs Value (Product)": Record<
    string,
    Record<string, { billing_quantity: number; invoice_amount: number }>
  >;
  "Plant Location Analysis": StringNumberMap;
  "Region Analysis": StringNumberMap;
  "Transaction Type Analysis": StringNumberMap;
}>;

export interface ReportingPeriodPayload {
  "IPE Testing"?: {
    "Total Invoices": number;
    "Total Invoice Amount"?: number;
    "Total Billing Quantity"?: number;
  };
  "Revenue Analytical Dashboard"?: RevenueDashboardBlock;
}

export type RfoDashboardRoot = Record<string, ReportingPeriodPayload>;

const EPS = 1e-9;

export function parseDdMmYyyy(key: string): number {
  const parts = key.trim().split(/[-/]/);
  if (parts.length !== 3) return 0;
  const d = Number(parts[0]);
  const m = Number(parts[1]) - 1;
  const y = Number(parts[2]);
  if (!y || m < 0 || m > 11) return 0;
  return new Date(y, m, d).getTime();
}

export function sortReportingDateKeys(keys: string[]): string[] {
  return [...keys].sort((a, b) => parseDdMmYyyy(a) - parseDdMmYyyy(b));
}

export function getPreviousReportingDate(sortedKeys: string[], selected: string): string | null {
  const i = sortedKeys.indexOf(selected);
  if (i <= 0) return null;
  return sortedKeys[i - 1] ?? null;
}

export function entriesSortedByValueDesc(m: StringNumberMap | undefined): [string, number][] {
  if (!m) return [];
  return Object.entries(m).sort((a, b) => b[1] - a[1]);
}

export function topNAndOthers(
  m: StringNumberMap | undefined,
  n: number,
): { top: [string, number][]; othersSum: number; othersLabel: string } {
  const entries = entriesSortedByValueDesc(m);
  const top = entries.slice(0, n);
  const othersSum = entries.slice(n).reduce((s, [, v]) => s + v, 0);
  return { top, othersSum, othersLabel: "Others" };
}

/** One bar per ranked row (by invoice amount), global rank 1 = highest. */
export interface RankBarRow {
  rank: number;
  value: number;
  /** Label for category axis (entity name, may be truncated in UI). */
  entityName: string;
}

/**
 * All ranked rows as separate values — one bar per rank (not aggregated into deciles).
 */
export function buildRankBarRows(m: StringNumberMap | undefined): RankBarRow[] {
  const entries = entriesSortedByValueDesc(m);
  return entries.map(([entityName, value], i) => ({
    rank: i + 1,
    value,
    entityName,
  }));
}

export function pctChange(prev: number, curr: number): number {
  const base = Math.max(Math.abs(prev), EPS);
  return ((curr - prev) / base) * 100;
}

export function absPctDiff(prev: number, curr: number): number {
  return Math.abs(pctChange(prev, curr));
}

export interface VarianceFlag {
  section: string;
  detail: string;
}

/** Flag pairs where relative change > threshold % (both sides meaningful). */
export function varianceOnStringNumberMap(
  section: string,
  cy: StringNumberMap | undefined,
  py: StringNumberMap | undefined,
  thresholdPct = 10,
): VarianceFlag[] {
  const flags: VarianceFlag[] = [];
  if (!cy || !py) return flags;
  const keys = new Set([...Object.keys(cy), ...Object.keys(py)]);
  for (const k of keys) {
    const a = cy[k] ?? 0;
    const b = py[k] ?? 0;
    if (Math.abs(a) < EPS && Math.abs(b) < EPS) continue;
    const diff = absPctDiff(b, a);
    if (diff > thresholdPct) {
      flags.push({
        section,
        detail: `${k}: ${Math.round(diff)}% vs prior period (${formatNum(b)} → ${formatNum(a)})`,
      });
    }
  }
  return flags;
}

export function varianceOnMoMQuantityValueTotal(
  cy: RevenueDashboardBlock["MoM Quantity vs Value (Total)"] | undefined,
  py: RevenueDashboardBlock["MoM Quantity vs Value (Total)"] | undefined,
  thresholdPct = 10,
): VarianceFlag[] {
  const flags: VarianceFlag[] = [];
  if (!cy || !py) return flags;
  const months = new Set([...Object.keys(cy), ...Object.keys(py)]);
  for (const m of months) {
    const c = cy[m];
    const p = py[m];
    if (!c || !p) continue;
    if (absPctDiff(p.invoice_amount, c.invoice_amount) > thresholdPct) {
      flags.push({
        section: "MoM Quantity vs Value (Total) — Amount",
        detail: `${m} invoice amount: ${Math.round(absPctDiff(p.invoice_amount, c.invoice_amount))}%`,
      });
    }
    if (absPctDiff(p.billing_quantity, c.billing_quantity) > thresholdPct) {
      flags.push({
        section: "MoM Quantity vs Value (Total) — Qty",
        detail: `${m} billing qty: ${Math.round(absPctDiff(p.billing_quantity, c.billing_quantity))}%`,
      });
    }
  }
  return flags;
}

export function aggregateVarianceFlags(
  dashCy: RevenueDashboardBlock | undefined,
  dashPy: RevenueDashboardBlock | undefined,
  thresholdPct = 10,
): VarianceFlag[] {
  if (!dashCy || !dashPy) return [];
  const out: VarianceFlag[] = [];
  const maps: [string, StringNumberMap | undefined][] = [
    ["MoM Trend", dashCy["MoM Trend"]],
    ["Customer Wise Analysis", dashCy["Customer Wise Analysis"]],
    ["Incoterm Wise Analysis", dashCy["Incoterm Wise Analysis"]],
    ["Export/Domestic Analysis", dashCy["Export/Domestic Analysis"]],
    ["Product Type Analysis", dashCy["Product Type Analysis"]],
    ["Plant Location Analysis", dashCy["Plant Location Analysis"]],
    ["Region Analysis", dashCy["Region Analysis"]],
    ["Transaction Type Analysis", dashCy["Transaction Type Analysis"]],
  ];
  const pyMaps: Record<string, StringNumberMap | undefined> = {
    "MoM Trend": dashPy["MoM Trend"],
    "Customer Wise Analysis": dashPy["Customer Wise Analysis"],
    "Incoterm Wise Analysis": dashPy["Incoterm Wise Analysis"],
    "Export/Domestic Analysis": dashPy["Export/Domestic Analysis"],
    "Product Type Analysis": dashPy["Product Type Analysis"],
    "Plant Location Analysis": dashPy["Plant Location Analysis"],
    "Region Analysis": dashPy["Region Analysis"],
    "Transaction Type Analysis": dashPy["Transaction Type Analysis"],
  };
  for (const [name, cur] of maps) {
    out.push(...varianceOnStringNumberMap(name, cur, pyMaps[name], thresholdPct));
  }
  out.push(...varianceOnMoMQuantityValueTotal(dashCy["MoM Quantity vs Value (Total)"], dashPy["MoM Quantity vs Value (Total)"], thresholdPct));
  return out.slice(0, 120);
}

export function formatNum(n: number): string {
  if (!Number.isFinite(n)) return "—";
  const rounded = Math.round(n);
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(rounded);
}

export function formatCompact(n: number): string {
  if (!Number.isFinite(n)) return "—";
  const rounded = Math.round(n);
  return new Intl.NumberFormat("en-IN", {
    notation: "compact",
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(rounded);
}

export function top10OverlapSummary(
  cy: StringNumberMap | undefined,
  py: StringNumberMap | undefined,
): { overlap: number; cyOnly: string[]; pyOnly: string[] } {
  const cyTop = new Set(topNAndOthers(cy, 10).top.map(([k]) => k));
  const pyTop = new Set(topNAndOthers(py, 10).top.map(([k]) => k));
  let overlap = 0;
  for (const k of cyTop) if (pyTop.has(k)) overlap++;
  const cyOnly = [...cyTop].filter((k) => !pyTop.has(k));
  const pyOnly = [...pyTop].filter((k) => !cyTop.has(k));
  return { overlap, cyOnly, pyOnly };
}

export function monthSortKey(month: string): number {
  const parts = month.split("-");
  if (parts.length < 2) return 0;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  return y * 12 + m;
}

export function sortMonthKeys(keys: string[]): string[] {
  return [...keys].sort((a, b) => monthSortKey(a) - monthSortKey(b));
}

/** Month-on-month product rate rows for dashboard tables (rate vs prior month in series). */
export interface ProductMomRateRow {
  product: string;
  month: string;
  rate: number;
  variancePct: number | null;
  /** |variance| &gt; 10% vs previous month in the product series */
  overTenPct: boolean;
}

export function buildProductRateMomTableRows(
  productRateMoM: Record<string, Record<string, number>> | undefined,
): ProductMomRateRow[] {
  if (!productRateMoM || typeof productRateMoM !== "object") return [];
  const out: ProductMomRateRow[] = [];
  for (const [product, monthMap] of Object.entries(productRateMoM)) {
    if (!monthMap || typeof monthMap !== "object") continue;
    const months = sortMonthKeys(Object.keys(monthMap));
    for (let i = 0; i < months.length; i++) {
      const month = months[i];
      const rate = Number(monthMap[month] ?? 0);
      let variancePct: number | null = null;
      let overTenPct = false;
      if (i > 0) {
        const prevRate = Number(monthMap[months[i - 1]] ?? 0);
        if (Math.abs(prevRate) > 1e-12) {
          variancePct = ((rate - prevRate) / prevRate) * 100;
          overTenPct = Math.abs(variancePct) > 10;
        }
      }
      out.push({ product, month, rate, variancePct, overTenPct });
    }
  }
  out.sort((a, b) => {
    const c = a.product.localeCompare(b.product);
    if (c !== 0) return c;
    return monthSortKey(a.month) - monthSortKey(b.month);
  });
  return out;
}
